import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  Tooltip,
  CircularProgress,
  TablePagination,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const SubcontractorManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  // eslint-disable-next-line no-unused-vars
  const { token } = useAuth();
  
  // State for subcontractor data
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // State for search
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for team form dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    email: '',
    phone: '',
    contactPerson: '',
    workType: '',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});
  
  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  
  // Fetch teams on component mount and when pagination/search changes
  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm]);
  
  // Fetch teams from API
  const fetchTeams = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/installation-teams', {
        params: {
          page: page + 1, // API uses 1-based indexing
          limit: rowsPerPage,
          search: searchTerm
        }
      });
      
      console.log('Subkontraktor API response:', response.data);
      
      if (response.data) {
        // Backend mengembalikan data dengan properti installationTeams, bukan teams
        setTeams(response.data.installationTeams || []);
        setTotalCount(response.data.installationTeams?.length || 0);
      }
    } catch (err) {
      console.error('Error fetching subcontractors:', err);
      setError('Gagal memuat data subkontraktor. Silakan coba lagi.');
      enqueueSnackbar('Gagal memuat data subkontraktor', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle dialog open for adding new team
  const handleAddTeam = () => {
    setFormData({
      id: null,
      name: '',
      email: '',
      phone: '',
      contactPerson: '',
      workType: '',
      isActive: true
    });
    setFormErrors({});
    setDialogMode('add');
    setOpenDialog(true);
  };
  
  // Handle dialog open for editing team
  const handleEditTeam = (team) => {
    setFormData({
      id: team.id,
      name: team.name || '',
      email: team.email || '',
      phone: team.phone || '',
      contactPerson: team.contactPerson || '',
      workType: team.workType || '',
      isActive: team.isActive
    });
    setFormErrors({});
    setDialogMode('edit');
    setOpenDialog(true);
  };
  
  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Handle switch change for isActive
  const handleSwitchChange = (e) => {
    setFormData(prev => ({
      ...prev,
      isActive: e.target.checked
    }));
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Nama subkontraktor wajib diisi';
    }
    
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Format email tidak valid';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submit
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      if (dialogMode === 'add') {
        // Create new team
        await api.post('/installation-teams', formData);
        enqueueSnackbar('Subkontraktor berhasil ditambahkan', { variant: 'success' });
      } else {
        // Update existing team
        await api.put(`/installation-teams/${formData.id}`, formData);
        enqueueSnackbar('Subkontraktor berhasil diperbarui', { variant: 'success' });
      }
      
      // Refresh teams list
      fetchTeams();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving subcontractor:', err);
      enqueueSnackbar('Gagal menyimpan data subkontraktor', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete confirmation dialog open
  const handleDeleteConfirmOpen = (team) => {
    setTeamToDelete(team);
    setDeleteDialogOpen(true);
  };
  
  // Handle delete confirmation dialog close
  const handleDeleteConfirmClose = () => {
    setDeleteDialogOpen(false);
    setTeamToDelete(null);
  };
  
  // Handle team deletion
  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;
    
    try {
      setLoading(true);
      
      await api.delete(`/installation-teams/${teamToDelete.id}`);
      enqueueSnackbar('Subkontraktor berhasil dihapus', { variant: 'success' });
      
      // Refresh teams list
      fetchTeams();
    } catch (err) {
      console.error('Error deleting subcontractor:', err);
      
      // Handle case where team is in use
      if (err.response && err.response.status === 400 && err.response.data.message.includes('digunakan')) {
        enqueueSnackbar('Subkontraktor tidak dapat dihapus karena sedang digunakan dalam proyek', { 
          variant: 'error',
          autoHideDuration: 5000
        });
      } else {
        enqueueSnackbar('Gagal menghapus subkontraktor', { variant: 'error' });
      }
    } finally {
      setLoading(false);
      handleDeleteConfirmClose();
    }
  };
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0); // Reset to first page when searching
  };
  
  // Handle refresh button click
  const handleRefresh = () => {
    setSearchTerm('');
    fetchTeams();
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Manajemen Subkontraktor
      </Typography>
      
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Cari subkontraktor..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={handleRefresh}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={handleAddTeam}
          >
            Tambah Subkontraktor
          </Button>
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>No</TableCell>
              <TableCell>Nama Subkontraktor</TableCell>
              <TableCell>Kontak Person</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Telepon</TableCell>
              <TableCell>Jenis Pekerjaan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && teams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="error">{error}</Typography>
                </TableCell>
              </TableRow>
            ) : teams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography>Tidak ada data subkontraktor tersedia</Typography>
                </TableCell>
              </TableRow>
            ) : (
              teams.map((team, index) => (
                <TableRow key={team.id}>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{team.name}</TableCell>
                  <TableCell>{team.contactPerson || '-'}</TableCell>
                  <TableCell>{team.email || '-'}</TableCell>
                  <TableCell>{team.phone || '-'}</TableCell>
                  <TableCell>{team.workType || '-'}</TableCell>
                  <TableCell>
                    {team.isActive ? (
                      <Typography color="success.main">Aktif</Typography>
                    ) : (
                      <Typography color="text.disabled">Tidak Aktif</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEditTeam(team)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Hapus">
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteConfirmOpen(team)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Baris per halaman:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} dari ${count}`}
      />
      
      {/* Add/Edit Team Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Tambah Subkontraktor Baru' : 'Edit Subkontraktor'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Nama Subkontraktor *"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
            error={!!formErrors.name}
            helperText={formErrors.name}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            name="contactPerson"
            label="Kontak Person"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.contactPerson}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleInputChange}
            error={!!formErrors.email}
            helperText={formErrors.email}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="phone"
            label="Telepon"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.phone}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="workType"
            label="Jenis Pekerjaan"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.workType}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label="Aktif"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteConfirmClose}>
        <DialogTitle>Konfirmasi Hapus</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Apakah Anda yakin ingin menghapus subkontraktor "{teamToDelete?.name}"? 
            Tindakan ini tidak dapat dibatalkan.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Batal</Button>
          <Button 
            onClick={handleDeleteTeam} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Hapus'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubcontractorManagement;
